import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSelector, useDispatch } from "react-redux";
import "./daily-inventory.css";
import { getFamily } from "../Redux/Actions/IngredientActions";

const NUMERIC_KEYPAD_KEYS = [
  ["1", "2", "3"],
  ["4", "5", "6"],
  ["7", "8", "9"],
  [".", "0", "⌫"],
];

const NumericKeypadModal = ({ show, title, value, onConfirm, onCancel }) => {
  const [buffer, setBuffer] = useState("");

  useEffect(() => {
    if (show) setBuffer(value === undefined || value === null ? "" : String(value));
  }, [show, value]);

  const handleKey = (k) => {
    if (k === "⌫") {
      setBuffer((s) => s.slice(0, -1));
      return;
    }
    if (k === ".") {
      if (buffer.includes(".")) return;
      if (buffer === "") {
        setBuffer("0.");
        return;
      }
    }
    if (buffer === "0" && k !== ".") {
      setBuffer(k);
      return;
    }
    setBuffer((s) => s + k);
  };

  const handleClear = () => setBuffer("");
  const handleConfirm = () => {
    const v = buffer.trim() === "" ? "" : buffer.replace(/^0+(\d)/, "$1");
    onConfirm(v);
    onCancel();
  };

  if (!show) return null;

  return (
    <div className="numeric-keypad-overlay" onClick={onCancel}>
      <div className="numeric-keypad-modal" onClick={(e) => e.stopPropagation()}>
        <div className="numeric-keypad-header">
          <span className="numeric-keypad-title">{title}</span>
          <button type="button" className="numeric-keypad-close" onClick={onCancel} aria-label="Fermer">
            ×
          </button>
        </div>
        <div className="numeric-keypad-display">{buffer || "0"}</div>
        <div className="numeric-keypad-grid">
          {NUMERIC_KEYPAD_KEYS.map((row, i) => (
            <div key={i} className="numeric-keypad-row">
              {row.map((k) => (
                <button key={k} type="button" className="numeric-keypad-key" onClick={() => handleKey(k)}>
                  {k}
                </button>
              ))}
            </div>
          ))}
        </div>
        <div className="numeric-keypad-actions">
          <button type="button" className="numeric-keypad-btn numeric-keypad-btn-clear" onClick={handleClear}>
            C
          </button>
          <button type="button" className="numeric-keypad-btn numeric-keypad-btn-ok" onClick={handleConfirm}>
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

const DailyInventory = () => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    date: getTodayDate(),
    location: "",
  });

  const [inventoryItems, setInventoryItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [stockData, setStockData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [scheduledIngredients, setScheduledIngredients] = useState([]);
  const [isLoadingScheduled, setIsLoadingScheduled] = useState(false);
  const [availableIngredients, setAvailableIngredients] = useState([]);
  const [keypad, setKeypad] = useState({ show: false, ingredientId: null, title: "" });

  const user = useSelector((state) => state.AuthReducer?.user);

  // Récupérer les familles depuis Redux
  const familiesData = useSelector((state) => state.IngredientReducer?.families || []);

  // Fonctions utilitaires pour les dates
  function getTodayDate() {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Helper pour extraire la localisation utilisateur
  const getUserLocationInfo = () => {
    if (!user) {
      return { id: null, name: "Non connecté" };
    }

    const loc = user.localisation;

    if (!loc) return { id: null, name: "Non spécifiée" };

    let locationId = null;
    let locationName = "Non spécifiée";

    if (loc.$oid) {
      locationId = String(loc.$oid);
      locationName = user.name || `Localisation ${loc.$oid}`;
    } else if (loc._id) {
      locationId = String(loc._id);
      locationName = user.name || `Localisation ${loc._id}`;
    } else if (typeof loc === 'string') {
      locationId = loc;
      locationName = user.name || `Localisation ${loc}`;
    } else {
      locationId = String(loc);
      locationName = user.name || `Localisation ${locationId}`;
    }

    return { id: locationId, name: locationName };
  };

  // Charger les données initiales
  useEffect(() => {
    fetchStockData();

    // Charger les familles si elles ne sont pas déjà chargées
    if (familiesData.length === 0) {
      dispatch(getFamily());
    }
  }, []);

  // Recharger et filtrer les locations quand l'utilisateur est chargé
  useEffect(() => {
    fetchLocations();
  }, [user]);

  useEffect(() => {
    fetchScheduledIngredients();
  }, []);

  const fetchStockData = async () => {
    try {
      const response = await axios.get("/api/Invoices/stock-report");
      console.log("Données stock chargées:", response.data.length);
      setStockData(response.data);
    } catch (error) {
      console.error("Erreur chargement stock:", error);
      showMessage("error", "Erreur lors du chargement des données de stock");
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get("/api/Locationspin/getAllLocations");
      let locationsData = response.data;
      let allLocations = [];

      if (Array.isArray(locationsData)) {
        allLocations = locationsData;
      } else if (locationsData && locationsData.locations) {
        allLocations = locationsData.locations;
      } else if (locationsData && locationsData.data) {
        allLocations = locationsData.data;
      }

      // Filtrer selon l'utilisateur
      const userLoc = getUserLocationInfo();
      if (userLoc.id) {
        // On ne garde que la localisation de l'utilisateur
        const userLocation = allLocations.find(l =>
          (l._id && String(l._id) === userLoc.id) ||
          (l.id && String(l.id) === userLoc.id)
        );

        if (userLocation) {
          setLocations([userLocation]);
          // Sélectionner automatiquement
          setFormData(prev => ({ ...prev, location: userLocation._id }));
        } else {
          // Tentative de filtrage plus lâche si l'ID ne correspond pas exactement
          const filtered = allLocations.filter(l => String(l._id) === userLoc.id);
          setLocations(filtered);
          if (filtered.length > 0) {
            setFormData(prev => ({ ...prev, location: filtered[0]._id }));
          }
        }
      } else {
        setLocations(allLocations);
      }

    } catch (error) {
      console.error("Erreur chargement localisations:", error);
      showMessage("error", "Erreur lors du chargement des localisations");
    }
  };

  const fetchScheduledIngredients = async () => {
    try {
      setIsLoadingScheduled(true);
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");

      if (!token) {
        console.error("Token non trouvé");
        return;
      }

      const response = await axios.get("/api/daily-inventory/schedule/all", {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log("Réponse planning complète:", response.data);

      if (response.data && response.data.success) {
        const scheduledItems = response.data.schedule || [];

        const activeScheduledItems = scheduledItems.filter(item => item.active === true);

        console.log("Ingrédients actifs:", activeScheduledItems);

        setScheduledIngredients(activeScheduledItems);

        // Mettre à jour les ingrédients disponibles si une localisation est déjà sélectionnée
        if (formData.location) {
          updateAvailableIngredients();
        }
      }
    } catch (error) {
      console.error("Erreur chargement ingrédients programmés:", error);
      showMessage("error", "Erreur lors du chargement du planning");
    } finally {
      setIsLoadingScheduled(false);
    }
  };

  // Fonction pour obtenir le nom complet d'un ingrédient
  const getIngredientName = (ingredientId) => {
    if (!ingredientId) return "Inconnu";

    const searchId = ingredientId.toString();

    // 1. Chercher d'abord dans scheduledIngredients
    const scheduledItem = scheduledIngredients.find(item =>
      item.ingredient && item.ingredient.toString() === searchId
    );

    if (scheduledItem) {
      // Si ingredientName est défini et n'est pas "Inconnu", l'utiliser
      if (scheduledItem.ingredientName && scheduledItem.ingredientName !== "Inconnu") {
        return scheduledItem.ingredientName;
      }
    }

    // 2. Chercher dans les familles
    for (const family of familiesData) {
      if (family.ingredients && Array.isArray(family.ingredients)) {
        const foundIngredient = family.ingredients.find(
          ing => ing._id && ing._id.toString() === searchId
        );
        if (foundIngredient && foundIngredient.name) {
          return foundIngredient.name;
        }
      }
    }

    // 3. Si scheduledItem existe mais n'a pas de nom, chercher par ID dans sa famille
    if (scheduledItem && scheduledItem.familyName) {
      return `${scheduledItem.familyName} - ${searchId.slice(-6)}`;
    }

    return `Ingrédient ${searchId.slice(-6)}`;
  };

  // Version corrigée de updateAvailableIngredients

  const getLocationInfo = (locationData) => {
    if (!locationData) return { name: "Non spécifiée", isValid: false };

    if (locationData.name && !locationData.name.includes('Localisation-')) {
      return { name: locationData.name, isValid: true };
    }

    let locationId;
    if (locationData._id) {
      locationId = locationData._id.toString();
    } else if (typeof locationData === 'string') {
      locationId = locationData;
    }

    if (locationId) {
      const foundLocation = locations.find(loc =>
        loc._id && loc._id.toString() === locationId
      );

      if (foundLocation && foundLocation.name) {
        return { name: foundLocation.name, isValid: true };
      }
    }

    return { name: "Localisation inconnue", isValid: false };
  };

  const updateAvailableIngredients = () => {
    if (!formData.location) {
      setAvailableIngredients([]);
      return;
    }

    const selectedLocation = formData.location.toString();
    const ingredients = [];

    // Filtrer les ingrédients programmés pour cette localisation
    const filteredIngredients = scheduledIngredients.filter(item => {
      const itemLocation = item.location ? item.location.toString() : null;
      // Debug log for checking why items might be filtered out
      // console.log("Debug item loc:", itemLocation, "Selected:", selectedLocation);
      return !itemLocation || itemLocation === selectedLocation;
    });

    filteredIngredients.forEach(scheduledItem => {
      const ingredientId = scheduledItem.ingredient;

      if (!ingredientId) return;

      // Obtenir le nom correct
      const ingredientName = getIngredientName(ingredientId);

      // Trouver le stock actuel
      let currentStock = 0;
      let unit = scheduledItem.unit || 'unité';

      const stockItem = stockData.find(item => {
        const itemIngredientId = item.ingredient?._id || item.ingredient;
        const itemLocationId = item.localisation?._id || item.localisation;

        return itemIngredientId?.toString() === ingredientId.toString() &&
          itemLocationId?.toString() === selectedLocation;
      });

      if (stockItem) {
        currentStock = stockItem.totalQuantity || 0;
        unit = stockItem.quantityUnit || unit;
      }

      // Vérifier si déjà dans l'inventaire
      const existingItem = inventoryItems.find(item =>
        item.ingredientId === ingredientId &&
        item.locationId === selectedLocation
      );

      ingredients.push({
        id: ingredientId,
        name: ingredientName,
        unit: unit,
        family: scheduledItem.familyName || "Inconnue",
        currentStock: currentStock,
        recordedQuantity: existingItem ? existingItem.recordedQuantity : "",
        difference: existingItem ? existingItem.difference : 0,
        notes: existingItem ? existingItem.notes : "",
        locationId: scheduledItem.location || formData.location,
        scheduleId: scheduledItem._id,
        frequency: scheduledItem.frequency,
        isInInventory: !!existingItem,
        inventoryItemId: existingItem ? existingItem.id : null
      });
    });

    setAvailableIngredients(ingredients.sort((a, b) => a.name.localeCompare(b.name)));
  };

  const handleLocationChange = async (e) => {
    const locationId = e.target.value;
    setFormData(prev => ({
      ...prev,
      location: locationId
    }));

    // Réinitialiser l'inventaire quand on change de localisation
    setInventoryItems([]);
  };

  const openKeypad = (ingredientId, title) => {
    if (saving) return;
    setKeypad({ show: true, ingredientId, title });
  };
  const closeKeypad = () => setKeypad((k) => ({ ...k, show: false }));
  const confirmKeypad = (value) => {
    if (keypad.ingredientId) handleInputChange(keypad.ingredientId, "recordedQuantity", value);
    closeKeypad();
  };

  const handleInputChange = (ingredientId, field, value) => {
    // Mettre à jour l'ingrédient dans availableIngredients
    const updatedIngredients = availableIngredients.map(ingredient => {
      if (ingredient.id === ingredientId) {
        const updatedIngredient = {
          ...ingredient,
          [field]: value
        };

        // Calculer la différence si on change la quantité enregistrée
        if (field === 'recordedQuantity') {
          const recordedQty = parseFloat(value) || 0;
          const currentQty = ingredient.currentStock || 0;
          updatedIngredient.difference = recordedQty - currentQty;
        }

        return updatedIngredient;
      }
      return ingredient;
    });

    setAvailableIngredients(updatedIngredients);
  };

  const addOrUpdateInventory = (ingredientId) => {
    const ingredient = availableIngredients.find(ing => ing.id === ingredientId);

    if (!ingredient) {
      showMessage("error", "Ingrédient non trouvé");
      return;
    }

    if (ingredient.recordedQuantity === "" || ingredient.recordedQuantity === undefined) {
      showMessage("error", "Veuillez saisir une quantité pour cet ingrédient");
      return;
    }

    const recordedQty = parseFloat(ingredient.recordedQuantity);

    if (isNaN(recordedQty)) {
      showMessage("error", "Veuillez saisir une quantité valide");
      return;
    }

    const locationInfo = getLocationInfo(formData.location);

    const inventoryItem = {
      id: ingredient.inventoryItemId || Date.now() + Math.random(),
      locationId: formData.location,
      locationName: locationInfo.name,
      ingredientId: ingredient.id,
      ingredientName: ingredient.name,
      currentStock: ingredient.currentStock,
      recordedQuantity: recordedQty,
      difference: ingredient.difference,
      unit: ingredient.unit,
      notes: ingredient.notes || "",
      scheduleId: ingredient.scheduleId,
      frequency: ingredient.frequency,
      addedAt: new Date().toISOString()
    };

    // Mettre à jour ou ajouter à l'inventaire
    if (ingredient.isInInventory) {
      setInventoryItems(prev =>
        prev.map(item => item.id === ingredient.inventoryItemId ? inventoryItem : item)
      );
      showMessage("success", "Article mis à jour");
    } else {
      setInventoryItems(prev => [...prev, inventoryItem]);
      // Mettre à jour le statut dans availableIngredients
      const updatedIngredients = availableIngredients.map(ing => {
        if (ing.id === ingredientId) {
          return {
            ...ing,
            isInInventory: true,
            inventoryItemId: inventoryItem.id
          };
        }
        return ing;
      });
      setAvailableIngredients(updatedIngredients);
      showMessage("success", "Article ajouté à l'inventaire");
    }
  };

  const saveInventory = async () => {
    if (inventoryItems.length === 0) {
      showMessage("error", "Aucun article à enregistrer");
      return;
    }

    if (!window.confirm(`Voulez-vous vraiment enregistrer cet inventaire de ${inventoryItems.length} articles ?`)) {
      return;
    }

    setSaving(true);

    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");

      if (!token) {
        showMessage("error", "Session expirée. Veuillez vous reconnecter.");
        setSaving(false);
        return;
      }

      // STRUCTURE SIMPLIFIÉE
      const inventoryData = {
        date: formData.date,
        items: inventoryItems.map(item => ({
          ingredient: item.ingredientId,
          location: item.locationId,
          currentStock: item.currentStock,
          recordedQuantity: item.recordedQuantity,
          difference: item.difference,
          unit: item.unit,
          notes: item.notes || "",
          scheduleId: item.scheduleId || null
        }))
      };

      console.log("📤 Envoi données:", JSON.stringify(inventoryData, null, 2));

      // IMPORTANT : Utiliser la route pour inventaire programmé
      // qui permet plusieurs inventaires par jour
      const response = await axios.post("/api/daily-inventory/schedule/inventory", inventoryData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log("✅ Réponse serveur:", response.data);

      if (response.data && response.data.success) {
        alert(`✅ Inventaire enregistré avec succès !\n${inventoryItems.length} articles sauvegardés.`);
        window.location.reload();
      } else {
        showMessage("error", response.data?.message || "Erreur lors de l'enregistrement");
      }

    } catch (error) {
      console.error("❌ Erreur sauvegarde:", error);

      if (error.response) {
        console.error("Status:", error.response.status);
        console.error("Data:", error.response.data);

        let errorMessage = `Erreur ${error.response.status}: `;

        if (error.response.data && error.response.data.message) {
          errorMessage += error.response.data.message;
        } else if (error.response.data && error.response.data.error) {
          errorMessage += error.response.data.error;
        } else {
          errorMessage += "Erreur serveur inconnue";
        }

        showMessage("error", errorMessage);
      } else {
        showMessage("error", "Erreur réseau ou serveur indisponible");
      }
    } finally {
      setSaving(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), 5000);
  };

  // Fonction pour obtenir le label de fréquence
  const getFrequencyLabel = (frequency) => {
    switch (frequency) {
      case 'daily': return 'Quotidien';
      case 'weekly': return 'Hebdomadaire';
      case 'biweekly': return 'Bimensuel';
      case 'monthly': return 'Mensuel';
      case 'quarterly': return 'Trimestriel';
      default: return frequency;
    }
  };

  // Fonction pour rafraîchir les données
  const refreshData = () => {
    fetchStockData();
    fetchScheduledIngredients();
    showMessage("info", "Données rafraîchies");
  };

  // Mettre à jour les ingrédients disponibles quand la localisation ou les données changent
  useEffect(() => {
    if (formData.location && scheduledIngredients.length > 0 && stockData.length > 0) {
      updateAvailableIngredients();
    }
  }, [formData.location, scheduledIngredients, stockData]);

  // Mettre à jour les ingrédients disponibles quand l'inventaire change
  useEffect(() => {
    if (formData.location && scheduledIngredients.length > 0 && stockData.length > 0) {
      updateAvailableIngredients();
    }
  }, [inventoryItems]);

  return (
    <div className="inventory-container">
      <div className="inventory-header">
        <div className="header-top">
          <h1>📋 Inventaire Journalier</h1>
          <button
            className="refresh-btn"
            onClick={refreshData}
            disabled={isLoadingScheduled}
            title="Rafraîchir les données"
          >
            {isLoadingScheduled ? '🔄 Chargement...' : '🔄 Rafraîchir'}
          </button>
        </div>
        <p>Saisie tactile des stocks physiques - Tableau unique de saisie</p>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      {isLoadingScheduled && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Chargement du planning d'inventaire...</p>
        </div>
      )}

      <div className="inventory-form">
        <div className="form-section">
          <h3>1. Configuration</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Date de l'inventaire *</label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                max={getTodayDate()}
              />
            </div>

            <div className="form-group">
              <label>Localisation *</label>
              <select
                name="location"
                value={formData.location}
                onChange={handleLocationChange}
              >
                <option value="">Sélectionner une localisation</option>
                {locations.map(location => {
                  const locationInfo = getLocationInfo(location);
                  return (
                    <option key={location._id} value={location._id}>
                      {locationInfo.name}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
        </div>

        {/* TABLEAU UNIQUE DE SAISIE */}
        {formData.location && (
          <div className="table-section">
            <div className="section-header">
              <h3>2. Saisie d'inventaire</h3>
              {formData.location && (
                <div className="location-info">
                  <strong>{getLocationInfo(formData.location).name}</strong>
                  {availableIngredients.length > 0 && (
                    <span className="items-count"> - {availableIngredients.length} ingrédients programmés</span>
                  )}
                </div>
              )}
            </div>

            {availableIngredients.length > 0 ? (
              <div className="saisie-table-container">
                <table className="saisie-table">
                  <thead>
                    <tr>
                      <th width="25%">Ingrédient</th>
                      <th width="10%">Stock système</th>
                      <th width="15%">Stock physique</th>
                      <th width="10%">Différence</th>
                      <th width="20%">Notes</th>
                      <th width="10%">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {availableIngredients.map(ingredient => (
                      <tr
                        key={ingredient.id}
                        className={ingredient.isInInventory ? 'in-inventory' : ''}
                        onClick={() => {
                          // Quand on clique sur la ligne, on met à jour automatiquement
                          if (ingredient.recordedQuantity) {
                            addOrUpdateInventory(ingredient.id);
                          }
                        }}
                      >
                        <td>
                          <div className="ingredient-cell">
                            <div className="ingredient-name">{ingredient.name}</div>
                            <div className="ingredient-details">
                              <span className="family">{ingredient.family}</span>
                              <span className="frequency">{getFrequencyLabel(ingredient.frequency)}</span>
                              <span className="unit">{ingredient.unit}</span>
                            </div>
                          </div>
                        </td>
                        <td className="stock-cell">
                          {ingredient.currentStock.toFixed(3)}
                        </td>
                        <td>
                          <div className="quantity-input-group">
                            <input
                              type="number"
                              inputMode="decimal"
                              value={ingredient.recordedQuantity}
                              onChange={(e) => handleInputChange(ingredient.id, 'recordedQuantity', e.target.value)}
                              step="0.001"
                              min="0"
                              placeholder="0.000"
                              className="quantity-input"
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  addOrUpdateInventory(ingredient.id);
                                }
                              }}
                            />
                            <button
                              type="button"
                              className="daily-inv-keypad-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                openKeypad(ingredient.id, `Stock physique · ${ingredient.name}`);
                              }}
                              disabled={saving}
                              title="Clavier numérique"
                              aria-label="Clavier numérique"
                            >
                              ⌨
                            </button>
                          </div>
                        </td>
                        <td className={`difference-cell ${ingredient.difference > 0 ? 'positive' : ingredient.difference < 0 ? 'negative' : ''}`}>
                          {ingredient.difference !== 0 ? (
                            <>
                              {ingredient.difference > 0 ? '+' : ''}
                              {ingredient.difference.toFixed(3)}
                            </>
                          ) : '-'}
                        </td>
                        <td>
                          <input
                            type="text"
                            value={ingredient.notes}
                            onChange={(e) => handleInputChange(ingredient.id, 'notes', e.target.value)}
                            placeholder="Notes..."
                            className="notes-input"
                          />
                        </td>
                        <td>
                          <div className={`status-badge ${ingredient.isInInventory ? 'added' : 'pending'}`}>
                            {ingredient.isInInventory ? '✅ Ajouté' : '⏳ Saisir quantité'}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="table-summary">
                  <div className="summary-info">
                    <span>Total articles saisis: <strong>{inventoryItems.length}</strong></span>
                    <span>Articles en attente: <strong>{availableIngredients.filter(ing => !ing.isInInventory).length}</strong></span>
                    <span>Différence totale:
                      <strong className={`total-difference ${inventoryItems.reduce((sum, item) => sum + item.difference, 0) >= 0 ? 'positive' : 'negative'}`}>
                        {inventoryItems.reduce((sum, item) => sum + item.difference, 0) >= 0 ? '+' : ''}
                        {inventoryItems.reduce((sum, item) => sum + item.difference, 0).toFixed(3)}
                      </strong>
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="no-ingredients">
                <div className="empty-icon">📋</div>
                <h4>Aucun ingrédient à inventorier</h4>
                <p>Aucun ingrédient n'est programmé pour cette localisation.</p>
                {scheduledIngredients.length === 0 && (
                  <div className="warning-box">
                    <strong>⚠️ Aucun ingrédient programmé</strong>
                    <p>Contactez l'administrateur pour programmer des inventaires.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {!formData.location && (
          <div className="select-location-prompt">
            <div className="prompt-icon">📍</div>
            <h4>Sélectionnez une localisation pour commencer</h4>
            <p>Veuillez choisir une localisation dans la liste ci-dessus pour afficher les ingrédients à inventorier.</p>
          </div>
        )}
      </div>

      {/* BOUTON DE SAUVEGARDE TOUJOURS VISIBLE SI DES ARTICLES */}
      {inventoryItems.length > 0 && (
        <div className="save-section">
          <div className="save-summary">
            <div className="summary-item">
              <span className="label">Articles à enregistrer:</span>
              <span className="value count">{inventoryItems.length}</span>
            </div>
            <div className="summary-item">
              <span className="label">Surplus:</span>
              <span className="value positive">
                +{inventoryItems.filter(item => item.difference > 0).length}
              </span>
            </div>
            <div className="summary-item">
              <span className="label">Manquants:</span>
              <span className="value negative">
                {inventoryItems.filter(item => item.difference < 0).length}
              </span>
            </div>
            <div className="summary-item">
              <span className="label">Différence totale:</span>
              <span className={`value total ${inventoryItems.reduce((sum, item) => sum + item.difference, 0) >= 0 ? 'positive' : 'negative'}`}>
                {inventoryItems.reduce((sum, item) => sum + item.difference, 0) >= 0 ? '+' : ''}
                {inventoryItems.reduce((sum, item) => sum + item.difference, 0).toFixed(3)}
              </span>
            </div>
          </div>

          <button
            className="save-button"
            onClick={saveInventory}
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="spinner"></span>
                Enregistrement en cours...
              </>
            ) : (
              '💾 ENREGISTRER L\'INVENTAIRE'
            )}
          </button>

          <div className="save-hint">
            <p>💡 Astuce: Cliquez sur une ligne après avoir saisi la quantité pour l'ajouter à l'inventaire</p>
          </div>
        </div>
      )}

      {formData.location && inventoryItems.length === 0 && availableIngredients.length > 0 && (
        <div className="save-section empty">
          <div className="empty-inventory-message">
            <div className="message-icon">📝</div>
            <h4>Aucun article saisi</h4>
            <p>Saisissez les quantités physiques dans le tableau ci-dessus, puis cliquez sur les lignes pour les ajouter à l'inventaire.</p>
          </div>
        </div>
      )}

      <NumericKeypadModal
        show={keypad.show}
        title={keypad.title}
        value={
          keypad.ingredientId
            ? (availableIngredients.find((i) => i.id === keypad.ingredientId)?.recordedQuantity ?? "")
            : ""
        }
        onConfirm={confirmKeypad}
        onCancel={closeKeypad}
      />
    </div>
  );
};

export default DailyInventory;